import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, Tag, Euro, MapPin, CheckCircle2, MessageCircle, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useSite } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { resolveImageUrl as resolveImg } from '@/lib/resolveImageUrl';

import { errorMessage } from '@/lib/errorMessage';

function formatItalianDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso + 'T00:00:00');
        return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
        return iso;
    }
}

function StatusBadge({ status }) {
    const styles = {
        'Disponibile': 'bg-atelier-sage/25 text-atelier-sage-ink border-atelier-sage/40',
        'Ultimi posti': 'bg-atelier-clay/40 text-foreground border-atelier-clay/50',
        'Sold out': 'bg-muted text-muted-foreground border-border',
    };
    return (
        <Badge
            data-testid={TEST_IDS.eventStatusBadge}
            variant="outline"
            className={cn('text-xs font-medium', styles[status] || styles['Disponibile'])}
        >
            {status}
        </Badge>
    );
}

function SignupDialog({ event, open, onClose, onSuccess }) {
    const { settings } = useSite();
    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '', phone: '', num_participants: 1, notes: '', privacy_consent: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({ first_name: '', last_name: '', email: '', phone: '', num_participants: 1, notes: '', privacy_consent: false });
            setErrors({});
            setConfirmation(null);
        }
    }, [open]);

    const nParticipants = Math.max(1, parseInt(form.num_participants, 10) || 1);
    const amountTotal = event?.price != null ? event.price * nParticipants : null;
    const whatsappDisplay = settings?.ws_whatsapp || settings?.phone_shop_value || settings?.whatsapp_number || '';

    const validate = () => {
        const e = {};
        if (!form.first_name.trim()) e.first_name = 'Necessario';
        if (!form.last_name.trim()) e.last_name = 'Necessario';
        if (!form.email.trim()) e.email = 'Necessario';
        else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Email non valida';
        if (!form.phone.trim()) e.phone = 'Necessario';
        else if (!/^[+\d\s\-()]{6,}$/.test(form.phone.trim())) e.phone = 'Numero non valido';
        const n = parseInt(form.num_participants, 10);
        if (!n || n < 1) e.num_participants = 'Almeno 1';
        if (event && n > event.available_seats) e.num_participants = `Massimo ${event.available_seats}`;
        if (!form.privacy_consent) e.privacy_consent = 'Devi accettare la privacy';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async () => {
        if (submitting) return; // guard against double click
        if (!validate() || !event) return;
        setSubmitting(true);
        try {
            const res = await api.post(`/events/${event.id}/register`, {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                num_participants: parseInt(form.num_participants, 10),
                notes: form.notes || null,
                privacy_consent: form.privacy_consent,
            });
            setConfirmation(res.data);
            onSuccess && onSuccess();
        } catch (err) {
            const msg = errorMessage(err, 'Qualcosa non ha funzionato. Riprova tra poco.');
            toast.error(typeof msg === 'string' ? msg : 'Errore durante l’iscrizione');
        } finally {
            setSubmitting(false);
        }
    };

    const money = (v) => (v == null ? '—' : `${Number(v).toFixed(2).replace('.', ',')} €`);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid={TEST_IDS.eventSignupDialog} className="max-w-lg max-h-[92vh] overflow-y-auto">
                {confirmation ? (
                    <div data-testid="event-signup-confirmation">
                        <DialogHeader>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-6 w-6 text-primary" />
                                <DialogTitle className="font-serif text-2xl">Richiesta registrata</DialogTitle>
                            </div>
                            <DialogDescription>
                                La tua richiesta di iscrizione è stata registrata. Il posto sarà confermato dopo la ricezione del pagamento e della contabile.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-3 rounded-xl border border-border bg-atelier-cream/40 p-4 text-sm space-y-1.5">
                            <Row label="Nome" value={confirmation.full_name} />
                            <Row label="Evento" value={confirmation.event_title} />
                            <Row label="Data" value={formatItalianDate(confirmation.event_date)} />
                            <Row label="Partecipanti" value={confirmation.num_participants} />
                            <Row label="Prezzo per persona" value={money(confirmation.price_per_person)} />
                            <Row label="Importo totale da pagare" value={<span className="font-semibold">{money(confirmation.amount_total)}</span>} />
                            {confirmation.payment_due_date && (
                                <Row label="Da versare entro" value={formatItalianDate(confirmation.payment_due_date)} />
                            )}
                            <div className="pt-1">
                                <Badge variant="outline" className="border-atelier-clay bg-atelier-clay/30 text-atelier-bronze text-xs">In attesa di pagamento</Badge>
                            </div>
                        </div>
                        {(event?.payment_instructions || settings?.ws_payment_instructions) && (
                            <div className="mt-3">
                                <p className="eyebrow mb-1 flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />Istruzioni per il pagamento</p>
                                <p className="text-sm text-muted-foreground whitespace-pre-line">{event?.payment_instructions || settings?.ws_payment_instructions}</p>
                            </div>
                        )}
                        {whatsappDisplay && (
                            <div className="mt-3 flex items-start gap-2 text-sm">
                                <MessageCircle className="h-4 w-4 text-atelier-sage-ink mt-0.5 shrink-0" />
                                <span className="text-muted-foreground">
                                    Dopo il pagamento inviaci la contabile su WhatsApp al numero <span className="font-medium text-foreground">{whatsappDisplay}</span>, indicando nome e cognome delle persone iscritte.
                                </span>
                            </div>
                        )}
                        <p className="mt-3 text-xs text-muted-foreground">Ti abbiamo inviato un’email con il riepilogo e le istruzioni. Controlla anche lo spam.</p>
                        <DialogFooter className="mt-4">
                            <Button data-testid="event-signup-confirm-close" onClick={onClose}>Ho capito</Button>
                        </DialogFooter>
                    </div>
                ) : (
                <>
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Iscriviti — {event?.title}</DialogTitle>
                    <DialogDescription>
                        {event && (
                            <span className="text-sm">
                                {formatItalianDate(event.date)} · {event.start_time}–{event.end_time} · Posti disponibili: {event.available_seats}
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="sig-first-name" className="eyebrow">Nome *</Label>
                            <Input id="sig-first-name" data-testid="event-signup-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={cn('mt-1.5', errors.first_name && 'border-destructive')} />
                            {errors.first_name && <p className="text-xs text-destructive mt-1">{errors.first_name}</p>}
                        </div>
                        <div>
                            <Label htmlFor="sig-last-name" className="eyebrow">Cognome *</Label>
                            <Input id="sig-last-name" data-testid="event-signup-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={cn('mt-1.5', errors.last_name && 'border-destructive')} />
                            {errors.last_name && <p className="text-xs text-destructive mt-1">{errors.last_name}</p>}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="sig-email" className="eyebrow">Email *</Label>
                            <Input id="sig-email" data-testid="event-signup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cn('mt-1.5', errors.email && 'border-destructive')} />
                            {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <Label htmlFor="sig-phone" className="eyebrow">Telefono *</Label>
                            <Input id="sig-phone" data-testid="event-signup-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cn('mt-1.5', errors.phone && 'border-destructive')} />
                            {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="sig-parts" className="eyebrow">Partecipanti *</Label>
                        <Input id="sig-parts" data-testid="event-signup-num" type="number" min={1} max={event?.available_seats || 1} value={form.num_participants} onChange={(e) => setForm({ ...form, num_participants: e.target.value })} className={cn('mt-1.5 w-32', errors.num_participants && 'border-destructive')} />
                        {errors.num_participants && <p className="text-xs text-destructive mt-1">{errors.num_participants}</p>}
                    </div>
                    {event?.price != null && (
                        <div className="rounded-lg border border-border bg-atelier-cream/40 px-4 py-3 text-sm" data-testid="event-signup-deposit-box">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Prezzo per persona</span>
                                <span>{money(event.price)}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-muted-foreground">Importo totale ({nParticipants} {nParticipants > 1 ? 'persone' : 'persona'})</span>
                                <span className="font-semibold" data-testid="event-signup-deposit-total">{money(amountTotal)}</span>
                            </div>
                            {event?.payment_deadline_days != null && (
                                <p className="text-xs text-muted-foreground mt-1.5">Da versare entro {event.payment_deadline_days} giorni dalla prenotazione.</p>
                            )}
                        </div>
                    )}
                    <div>
                        <Label htmlFor="sig-notes" className="eyebrow">Note (opzionale)</Label>
                        <Textarea id="sig-notes" data-testid="event-signup-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Eventuali allergie, esperienze, richieste…" />
                    </div>
                    <div className="flex items-start gap-3">
                        <Checkbox id="sig-priv" data-testid="event-signup-privacy" checked={form.privacy_consent} onCheckedChange={(c) => setForm({ ...form, privacy_consent: !!c })} />
                        <label htmlFor="sig-priv" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                            Acconsento al trattamento dei miei dati per gestire questa iscrizione, come descritto nell'<Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">informativa privacy</Link>. *
                        </label>
                    </div>
                    {errors.privacy_consent && <p className="text-xs text-destructive -mt-2">{errors.privacy_consent}</p>}
                </div>
                <DialogFooter className="mt-4 gap-2">
                    <Button variant="ghost" onClick={onClose}>Annulla</Button>
                    <Button data-testid={TEST_IDS.eventSignupSubmit} onClick={onSubmit} disabled={submitting}>
                        {submitting ? 'Un momento…' : 'Invia iscrizione'}
                    </Button>
                </DialogFooter>
                </>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right">{value}</span>
        </div>
    );
}

export default function Eventi() {
    const { t } = useSite();
    useSeo({
        title: `${SITE_NAME} — Eventi e workshop`,
        description: 'Eventi e workshop floreali a Lomazzo: bouquet, composizioni, corone, atelier serali. Posti limitati, iscriviti online.',
    });

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);

    const loadEvents = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/events');
            setEvents(res.data || []);
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[Eventi] Failed to load events:', err);
            }
            toast.error('Impossibile caricare gli eventi');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    return (
        <>
            <section className="pt-32 sm:pt-40 pb-6">
                <div className="container-atelier max-w-3xl">
                    <p className="eyebrow mb-4">{t('eventi.hero.eyebrow', 'Eventi')}</p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        {t('eventi.hero.title', 'Esperienze in Atelier.')}
                    </h1>
                    <p className="mt-5 text-muted-foreground leading-relaxed text-base sm:text-lg">
                        {t('eventi.hero.description', 'Workshop, incontri e serate a tema: vivi Floreal come un laboratorio di idee e emozioni.')}
                    </p>
                </div>
            </section>

            <Section className="pt-6 sm:pt-8">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={`event-skeleton-${i}`} className="rounded-xl bg-muted h-[420px] animate-pulse" />
                        ))}
                    </div>
                ) : events.length === 0 ? (
                    <div className="text-center py-16">
                        <p className="text-muted-foreground">{t('eventi.empty', 'Nessun evento in programma al momento. Torna presto.')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                        {events.map((e, i) => (
                            <motion.article
                                key={e.id}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                                data-testid={TEST_IDS.eventCard}
                                className="rounded-xl border border-border bg-card overflow-hidden flex flex-col"
                            >
                                <div className="aspect-[5/3] overflow-hidden bg-muted">
                                    {e.image_url ? (
                                        <img
                                            src={resolveImg(e.image_url)}
                                            alt={e.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                            style={{
                                                transform: `scale(${e.image_scale ?? 1}) translate(${e.image_offset_x ?? 0}%, ${e.image_offset_y ?? 0}%)`,
                                                transformOrigin: 'center center',
                                            }}
                                        />
                                    ) : <div className="w-full h-full bg-secondary" />}
                                </div>
                                <div className="p-6 flex flex-col flex-1">
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="eyebrow">{formatItalianDate(e.date)}</p>
                                        <StatusBadge status={e.status} />
                                    </div>
                                    <div className="flex items-center gap-2 mt-2">
                                        <h3 className="font-serif text-2xl tracking-tight">{e.title}</h3>
                                    </div>
                                    <Badge variant="outline" className="mt-1.5 w-fit text-[10px] font-normal capitalize border-atelier-sage/50 bg-atelier-sage/10 text-atelier-sage-ink">
                                        {e.event_type === 'aperiworkshop' ? 'Aperiworkshop' : 'Workshop'}
                                    </Badge>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{e.start_time} – {e.end_time}</span>
                                        <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{e.available_seats} / {e.total_seats} posti</span>
                                        {e.price != null && (
                                            <span className="inline-flex items-center gap-1.5"><Euro className="h-3.5 w-3.5" />{e.price.toFixed(0)} €</span>
                                        )}
                                        {e.price != null && e.payment_deadline_days != null && (
                                            <span className="inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />pagamento entro {e.payment_deadline_days} gg</span>
                                        )}
                                    </div>
                                    {e.location && (
                                        <p className="mt-2 inline-flex items-start gap-1.5 text-xs text-muted-foreground">
                                            <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                            <span className="leading-snug">{e.location}</span>
                                        </p>
                                    )}
                                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1 whitespace-pre-line">{e.description}</p>
                                    <Button
                                        onClick={() => setActive(e)}
                                        disabled={e.status === 'Sold out' || e.available_seats <= 0}
                                        data-testid={TEST_IDS.eventSignupButton}
                                        className="mt-5 h-11"
                                    >
                                        {(e.status === 'Sold out' || e.available_seats <= 0) ? 'Evento completo' : 'Iscriviti'}
                                    </Button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </Section>

            <SignupDialog
                event={active}
                open={!!active}
                onClose={() => setActive(null)}
                onSuccess={loadEvents}
            />
        </>
    );
}
